import styled from "styled-components";
import { Button, Modal, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 48px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-top: 32px;
    margin-bottom: 16px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    margin-top: 24px;
    margin-bottom: 12px;
    flex-direction: column;
    gap: 8px;
  }
`;

const FeeList = styled.div`
  margin-top: 8px;
  margin-left: 16px;
  max-height: 210px;
  overflow-y: auto;
  padding-right: 8px;

  /* Custom scrollbar styling for better appearance */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FeeName = styled.div`
  margin: 4px 0;
  display: flex;

  &::before {
    content: '"';
    flex-shrink: 0;
  }

  &::after {
    content: '"';
    flex-shrink: 0;
  }

  /* Typography wrapper that handles ellipsis */
  & > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`;

export interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  feeNames: string[];
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: "primary" | "danger";
}

export const ConfirmationModal = ({
  isOpen,
  title,
  feeNames,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText,
  confirmVariant = "danger"
}: ConfirmationModalProps) => {
  const message = String(
    Locale.getText(
      feeNames.length === 1
        ? "FEEMANAGER_DELETECONF_TEXT"
        : "FEEMANAGER_DELETECONF_BULK_TEXT"
    )
  );

  const cancelLabel = cancelText || String(Locale.getText("FEEMANAGER_CANCEL"));

  // Prevent closing on backdrop click but allow X button and ESC key
  const handleModalHide = ((...args: unknown[]) => {
    const reason = args[1] as string | undefined;
    if (reason === "backdropClick") {
      return;
    }
    onCancel();
  }) as () => void;

  return (
    <Modal header={title} show={isOpen} size="small" onHide={handleModalHide}>
      <Typography variant={"body-md"}>{message}</Typography>
      <FeeList>
        {/* eslint-disable react/no-array-index-key */}
        {feeNames.map((name, index) => (
          <FeeName key={`${name}-${index}`}>
            <Typography variant={"body-md"}>{name}</Typography>
          </FeeName>
        ))}
        {/* eslint-enable react/no-array-index-key */}
      </FeeList>
      <ButtonGroup>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="primary"
          onClick={onConfirm}
        >
          {confirmText}
        </Button>
      </ButtonGroup>
    </Modal>
  );
};
